/**
 * PageLayout - Unified page wrapper with panel support
 *
 * Provides a consistent three-column layout system:
 * - Secondary Panel (optional): 280px collapsible panel between sidebar and content
 * - Main Content: flex-1 fills remaining space
 * - Right Panel (optional): 400px overlay/drawer for detail panels
 *
 * Content width variants:
 * - full-width: No constraints, fills available space
 * - constrained: max-w-7xl with padding
 * - centered: max-w-4xl centered with padding
 */

import { type ReactNode, createContext, useContext } from 'react';

// Layout context for nested components to access layout state
interface PageLayoutContextValue {
  variant: 'full-width' | 'constrained' | 'centered';
  hasSecondaryPanel: boolean;
  hasRightPanel: boolean;
  isRightPanelOpen: boolean;
}

const PageLayoutContext = createContext<PageLayoutContextValue>({
  variant: 'full-width',
  hasSecondaryPanel: false,
  hasRightPanel: false,
  isRightPanelOpen: false,
});

export const usePageLayout = () => useContext(PageLayoutContext);

export interface PageLayoutProps {
  children: ReactNode;

  /** Content width variant */
  variant?: 'full-width' | 'constrained' | 'centered';

  /** Secondary panel (left side, after sidebar) */
  secondaryPanel?: ReactNode;

  /** Right panel content (overlay or inline) */
  rightPanel?: ReactNode;

  /** Width of right panel in pixels (default: 400) */
  rightPanelWidth?: number;

  /** Right panel display mode */
  rightPanelMode?: 'overlay' | 'inline' | 'drawer';

  /** Whether right panel is open */
  isRightPanelOpen?: boolean;

  /** Callback when right panel should close */
  onRightPanelClose?: () => void;

  /** Page header content */
  header?: ReactNode;

  /** Whether header should stick to top on scroll */
  stickyHeader?: boolean;

  /** Additional className for the content area */
  contentClassName?: string;

  /** Background color for content area */
  contentBackground?: string;
}

export function PageLayout({
  children,
  variant = 'full-width',
  secondaryPanel,
  rightPanel,
  rightPanelWidth = 400,
  rightPanelMode = 'overlay',
  isRightPanelOpen = false,
  onRightPanelClose,
  header,
  stickyHeader = false,
  contentClassName = '',
  contentBackground = 'rgb(var(--c-surface-muted, 251 250 248))',
}: PageLayoutProps) {
  const contextValue: PageLayoutContextValue = {
    variant,
    hasSecondaryPanel: !!secondaryPanel,
    hasRightPanel: !!rightPanel,
    isRightPanelOpen,
  };

  // Content width classes based on variant
  const contentWidthClasses = {
    'full-width': 'w-full',
    'constrained': 'max-w-7xl mx-auto px-6',
    'centered': 'max-w-4xl mx-auto px-6',
  };

  return (
    <PageLayoutContext.Provider value={contextValue}>
      <div className="h-full flex flex-col" style={{ backgroundColor: contentBackground }}>
        {/* Header - optionally sticky */}
        {header && (
          <div className={stickyHeader ? 'sticky top-0 z-30' : ''}>
            {header}
          </div>
        )}

        {/* Main content area with optional secondary panel */}
        <div className="flex-1 flex min-h-0">
          {/* Secondary Panel */}
          {secondaryPanel && (
            <div className="flex-shrink-0">
              {secondaryPanel}
            </div>
          )}

          {/* Main Content */}
          <main
            className={`flex-1 flex flex-col min-w-0 overflow-auto ${contentClassName}`}
          >
            <div className={`flex-1 ${variant !== 'full-width' ? contentWidthClasses[variant] : 'h-full'}`}>
              {children}
            </div>
          </main>

          {/* Right Panel - Inline mode */}
          {rightPanel && rightPanelMode === 'inline' && isRightPanelOpen && (
            <div
              className="flex-shrink-0 border-l border-line bg-white"
              style={{ width: rightPanelWidth }}
            >
              {rightPanel}
            </div>
          )}
        </div>

        {/* Right Panel - Overlay/Drawer mode */}
        {rightPanel && (rightPanelMode === 'overlay' || rightPanelMode === 'drawer') && isRightPanelOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/30 z-40 transition-opacity"
              onClick={onRightPanelClose}
              aria-hidden="true"
            />

            {/* Panel */}
            <div
              className={`
                fixed inset-y-0 right-0 z-50 bg-white shadow-2xl
                transform transition-transform duration-300 ease-out
                ${rightPanelMode === 'drawer' ? 'rounded-l-2xl' : ''}
              `}
              style={{ width: rightPanelWidth }}
            >
              {rightPanel}
            </div>
          </>
        )}
      </div>
    </PageLayoutContext.Provider>
  );
}

export default PageLayout;
